import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { loadJuniorRoster } from "../../lib/mentorRoster";

// ── Styled components (inline, mirroring sales-dashboard/OverviewTab.js) ──────

const HeroRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin: 8px 0 32px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const HeroCell = styled.button`
  position: relative;
  padding: 4px 0 4px 14px;
  border: none;
  border-left: 1px solid var(--nd-border-visible);
  background: transparent;
  color: inherit;
  text-align: left;
  font: inherit;
  cursor: default;
  &:focus { outline: 1px dashed var(--nd-text-display); outline-offset: 2px; }
`;

const HeroNumber = styled.div`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 56px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1;
`;

const StageLabel = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-top: 8px;
`;

const Divider = styled.div`
  border-top: 1px solid var(--nd-border);
  margin: 32px 0 24px;
`;

const SubTitle = styled.h2`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 24px 0 16px;
`;

const FunnelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const bucketColor = (label) => {
  if (label === "100%") return "var(--nd-success)";
  if (label === "75–99%") return "var(--nd-warning)";
  return "var(--nd-border-visible)";
};

const FunnelRow = styled.div`
  position: relative;
  height: 32px;
  border: 1px solid var(--nd-border);
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  background: linear-gradient(
    to right,
    ${({ bucket }) => bucketColor(bucket)} 0%,
    ${({ bucket }) => bucketColor(bucket)} ${({ pct }) => pct}%,
    transparent ${({ pct }) => pct}%,
    transparent 100%
  );
`;

const RowLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-display);
  mix-blend-mode: difference;
`;

const RowValue = styled.span`
  margin-left: auto;
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--nd-text-display);
  mix-blend-mode: difference;
`;

const AtRiskList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AtRiskItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid var(--nd-border);
  border-radius: 4px;
  font-family: 'Space Mono', monospace;
  font-size: 12px;
`;

const AtRiskName = styled.button`
  background: none;
  border: none;
  color: var(--nd-text-display);
  font: inherit;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 3px;
  &:hover { color: var(--nd-text-secondary); }
`;

const AtRiskReason = styled.span`
  color: var(--nd-text-disabled);
  margin-left: auto;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Empty = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  text-align: center;
  margin: 64px 0;
`;

// ── Progress distribution buckets ─────────────────────────────────────────────

const BUCKETS = [
  { label: "0–25%", test: (p) => p < 25 },
  { label: "25–50%", test: (p) => p >= 25 && p < 50 },
  { label: "50–75%", test: (p) => p >= 50 && p < 75 },
  { label: "75–99%", test: (p) => p >= 75 && p < 100 },
  { label: "100%", test: (p) => p === 100 },
];

// ── Component ─────────────────────────────────────────────────────────────────

const MentorOverviewTab = ({ onOpenJunior }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadJuniorRoster()
      .then((data) => {
        setRows(data);
        setLoading(false);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[mentor-dashboard]", err);
        setError(err && err.code ? err.code : "load failed");
        setLoading(false);
      });
  }, []);

  // Hero card computations (always run — SSR-safe, above early returns)
  const totalJuniors = rows.length;
  const avgProgress = rows.length
    ? Math.round(rows.reduce((acc, r) => acc + r.pct, 0) / rows.length)
    : 0;
  const activeStreaks = rows.filter((r) => r.streakDays > 0).length;
  const mentorQsToday = rows.reduce((acc, r) => acc + (r.mentorQsToday || 0), 0);
  const completedPaths = rows.filter((r) => r.pct === 100).length;

  // Progress distribution
  const bucketCounts = BUCKETS.map((b) => ({
    label: b.label,
    count: rows.filter((r) => b.test(r.pct)).length,
  }));
  const maxBucketCount = Math.max(1, ...bucketCounts.map((b) => b.count));

  // At-risk list
  const atRisk = rows.filter((r) => r.streakDays === 0 || r.pct < 20).map((r) => ({
    uid: r.uid,
    name: r.displayName,
    reason: r.streakDays === 0 ? "no streak" : "low progress",
  }));

  if (loading) return <Empty>loading…</Empty>;
  if (error) return <Empty>error · {error}</Empty>;

  return (
    <>
      {/* Hero cards */}
      <HeroRow>
        <HeroCell type="button" aria-label="Total juniors">
          <HeroNumber>{totalJuniors}</HeroNumber>
          <StageLabel>Juniors</StageLabel>
        </HeroCell>
        <HeroCell type="button" aria-label="Average progress">
          <HeroNumber>{avgProgress}%</HeroNumber>
          <StageLabel>Avg progress</StageLabel>
        </HeroCell>
        <HeroCell type="button" aria-label="Active streaks">
          <HeroNumber>{activeStreaks}</HeroNumber>
          <StageLabel>Active streaks</StageLabel>
        </HeroCell>
        <HeroCell type="button" aria-label="Mentor questions today">
          <HeroNumber>{mentorQsToday}</HeroNumber>
          <StageLabel>Mentor Qs today</StageLabel>
        </HeroCell>
        <HeroCell type="button" aria-label="Completed paths">
          <HeroNumber>{completedPaths}</HeroNumber>
          <StageLabel>Completed paths</StageLabel>
        </HeroCell>
      </HeroRow>

      <Divider />

      {/* Progress distribution */}
      <SubTitle>Progress distribution</SubTitle>
      <FunnelStack>
        {bucketCounts.map(({ label, count }) => (
          <FunnelRow
            key={label}
            bucket={label}
            pct={(count / maxBucketCount) * 100}
          >
            <RowLabel>{label}</RowLabel>
            <RowValue>{count}</RowValue>
          </FunnelRow>
        ))}
      </FunnelStack>

      <Divider />

      {/* At-risk list */}
      <SubTitle>At-risk juniors</SubTitle>
      {atRisk.length === 0 ? (
        <Empty>No at-risk juniors</Empty>
      ) : (
        <AtRiskList>
          {atRisk.map(({ uid, name, reason }) => (
            <AtRiskItem key={uid}>
              <AtRiskName
                type="button"
                onClick={() => onOpenJunior && onOpenJunior(uid)}
              >
                {name}
              </AtRiskName>
              <AtRiskReason>{reason}</AtRiskReason>
            </AtRiskItem>
          ))}
        </AtRiskList>
      )}
    </>
  );
};

export default MentorOverviewTab;
